import { useContext, useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"

//SOAR Components
import ParticipantSidebar from "../../../components/sidebars/ParticipantSidebar"
import PageSidebarContent from "../../../components/sidebars/PageSidebarContent"

//PrimeReact
import { Button } from "primereact/button"
import { TabPanel, TabView } from "primereact/tabview"

//styling
import assessStyles from "../../../styles/assessments/Assessment.module.css"
import myStyles from "../../../styles/assessments/ParticipantAssessments.module.css"

//Helpers
import { LogError } from "../../../helpers/logger"
import isParticipantActive from "../../../helpers/isParticipantActive"

//GraphQL
import { useLazyQuery } from "@apollo/client"
import { GET_LATEST_SERVICE_EPISODE, GET_STAFF_LIST } from "../queries"

// Context
import { UserContext } from "../../../context/userContext"

//Assessment components
import ParticipantAssessmentsSchedules from "./ParticipantAssessmentsSchedules"
import AssessmentScheduleForm from "../AssessmentScheduleForm/AssessmentScheduleForm"
import ParticipantAssessmentsCompleted from "./ParticipantAssessmentsCompleted"
import ParticipantAssessmentsOverview from "./ParticipantAssessmentsOverview"
import AssessmentForm from "../AssessmentForm/AssessmentForm"
import { telerecoveryApiGetIsActive } from "../telerecoveryApi"
import ParticipantAssessmentBlockDialog from "./ParticipantAssessmentBlockDialog"
import { assessmentsApiListMiniPost } from "../assessmentsApi"
import InactiveParticipantToolTip from "./InactiveParticipantToolTip"
import { KIOSK_MEMBER } from "../../../config/globalsStatus"

export default function ParticipantAssessments() {
  const userCtx = useContext(UserContext)
  const location = useLocation()
  const hasParticipantSelection = userCtx.participant_id
  const participantIsActive = isParticipantActive(
    userCtx.participant_membership_status,
  )
  const allowNewAssessment =
    hasParticipantSelection &&
    (participantIsActive ||
      userCtx.participant_membership_status === KIOSK_MEMBER)

  const [tabViewIndex, setTabViewIndex] = useState(0)
  const [assessmentProfiles, setAssessmentsProfiles] = useState([])

  //Assessment block values:
  const [showAssessmentBlock, setShowAssessmentBlock] = useState(false)
  const [scheduledTaskId, setScheduledTaskId] = useState(undefined)

  //Assessment form values:
  const [newAssessmentId, setNewAssessmentId] = useState(undefined)
  const [completedId, setCompletedId] = useState(undefined)
  const [openReadOnly, setOpenReadOnly] = useState(true)
  const [showAssessmentForm, setShowAssessmentForm] = useState(false)

  //Assessment schedule form values
  const [showScheduleForm, setShowScheduleForm] = useState(false)
  const [scheduleProfileId, setScheduleProfile] = useState(undefined)
  const [activeTelerecovery, setActiveTelerecovery] = useState(false)

  const [triggerRefresh, setTriggerRefresh] = useState(false)
  const [participantServiceEpisode, setParticipantServiceEpisode] = useState({
    isLoading: true,
  })
  const [staff, setStaff] = useState(undefined)

  const facilitatorProfilesRef = useRef([])

  const [getParticipant] = useLazyQuery(GET_LATEST_SERVICE_EPISODE, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: "",
      participant_id: "",
      organization_id: "",
      participant_membership_id: "",
    },
  })

  const [loadStaff] = useLazyQuery(GET_STAFF_LIST, {
    fetchPolicy: "cache-and-network",
    skip: true,
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
  })

  useEffect(() => {
    if (location.state) {
      if (
        location.state.taskId &&
        location.state?.participant_id === userCtx.participant_id
      ) {
        //Open the Scheduled assessments tab view
        setTabViewIndex(2)
        //Show the task form to complete the task
        updateShowAssessmentBlock(true, location.state.taskId)
      } else if (location.state.page) {
        switch (location.state.page) {
          case "schedule":
            setTabViewIndex(2)
            break
          default:
            break
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  useEffect(() => {
    if (userCtx.participant_id) {
      getParticipant({
        variables: {
          staff_id: userCtx.session.user.staff_id,
          participant_id: userCtx.participant_id,
          organization_id: userCtx.session.user.organization_id,
          participant_membership_id: userCtx.participant_membership_id,
        },
      })
        .then((result) => {
          if (result.data) {
            setParticipantServiceEpisode(result.data)
          } else {
            LogError("Unable to retrieve participant data", result)
            userCtx.setNotificationError("Unable to retrieve participant data")
            setParticipantServiceEpisode(undefined)
          }
        })
        .catch((error) => {
          LogError("Unable to retrieve participant data", error)
          userCtx.setNotificationError("Unable to retrieve participant data")
          setParticipantServiceEpisode(undefined)
        })

      assessmentsApiListMiniPost(userCtx.tokens.getAssessmentsToken(), {
        organizationId: userCtx.session.user.organization_id,
        enterpriseId: userCtx.session.user.enterprise_id,
      })
        .then((result) => {
          setAssessmentsProfiles(result.assessments)
        })
        .catch((error) => {
          LogError("Unable to retrieve assessment list data", error)
          userCtx.setNotificationError("Unable to retrieve assessment profiles")
        })
    }
    telerecoveryApiGetIsActive(
      userCtx.tokens.getTelerecoveryToken(),
      userCtx.session.user.organization_alias,
    )
      .then((result) => {
        setActiveTelerecovery(result)
      })
      .catch((error) => {
        setActiveTelerecovery(false)
        LogError(
          "Unable to confirm if SMS is active for this organization",
          error,
        )
        userCtx.setNotificationError(
          "Unable to confirm if SMS is active for this organization",
        )
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.participant_id, userCtx.session.user.organization_id])

  useEffect(() => {
    loadStaff({
      variables: {
        staff_id: userCtx.session.user.staff_id,
        organization_id: userCtx.session.user.organization_id,
      },
    })
      .then((result) => {
        if (result.data && result.data.users) {
          let cleanUsers = result.data.users.map((user) => {
            return {
              staff_id: user.staff_id,
              staff_name: `${user.staff_name_first} ${user.staff_name_last}`,
              staff_email_address: user.staff_email_address,
              staff_phone_number: user.staff_phone_number,
              organization_id: user.organization_id,
            }
          })

          if (
            !cleanUsers.find(
              (user) => user.staff_id === userCtx.session.user.staff_id,
            )
          ) {
            cleanUsers = [
              {
                staff_id: userCtx.session.user.staff_id,
                staff_name: `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`,
                staff_email_address: userCtx.session.user.staff_email_address,
                staff_phone_number: userCtx.session.user.staff_phone_number,
              },
              ...cleanUsers,
            ].sort((a, b) => (a.staff_name > b.staff_name ? 1 : -1))
          }

          if (facilitatorProfilesRef.current) {
            let facilitatorList = cleanUsers.filter(
              (user) =>
                !facilitatorProfilesRef.current.some(
                  (facilitator) =>
                    facilitator.staff_id === user.staff_id &&
                    facilitator.organization_id === user.organizationId,
                ),
            )

            facilitatorList = facilitatorList.map((user) => {
              return {
                profile_id: user.staff_id,
                organization_id: user.organization_id,
                name: user.staff_name,
              }
            })

            facilitatorProfilesRef.current = [
              ...facilitatorProfilesRef.current,
              ...facilitatorList,
            ]
          } else {
            facilitatorProfilesRef.current = cleanUsers.map((user) => {
              return {
                profile_id: user.staff_id,
                organization_id: user.organization_id,
                name: user.staff_name,
              }
            })
          }

          setStaff(cleanUsers.length === 0 ? undefined : cleanUsers)
        } else {
          LogError("Unable to retrieve staff list", result)
          userCtx.setNotificationError("Unable to retrieve staff list")

          setStaff(undefined)
        }
      })
      .catch((error) => {
        LogError("Unable to retrieve staff list", error)
        userCtx.setNotificationError("Unable to retrieve staff list")

        setStaff(undefined)
        setStaff(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session.user.organization_id])

  const updateShowAssessmentBlock = (showBlock, showScheduledTaskId) => {
    if (showBlock) {
      setScheduledTaskId(showScheduledTaskId)
      setShowAssessmentBlock(true)
    } else {
      setShowAssessmentBlock(false)
      setScheduledTaskId(undefined)
    }
  }

  const setAssessmentId = (id, isCompletedId, readOnly) => {
    if (!isCompletedId) {
      setNewAssessmentId(id)
      setCompletedId(undefined)
      setOpenReadOnly(false)
      setShowAssessmentForm(true)
    } else {
      setNewAssessmentId(undefined)
      setCompletedId(id)
      setOpenReadOnly(readOnly || true)
      setShowAssessmentForm(true)
    }
  }

  const onSaveFacilitator = (profile_id, organization_id, name) => {
    let currentFacilitators = facilitatorProfilesRef.current

    if (
      !currentFacilitators.some(
        (profile) =>
          profile.profile_id === profile_id &&
          profile.organization_id === organization_id,
      )
    ) {
      currentFacilitators = [
        ...currentFacilitators,
        { profile_id, organization_id, name },
      ]

      facilitatorProfilesRef.current = currentFacilitators
    }

    return facilitatorProfilesRef.current
  }

  return (
    <>
      <PageSidebarContent>
        <ParticipantSidebar />
      </PageSidebarContent>
      <div className="main-content">
        {showAssessmentBlock ? (
          <ParticipantAssessmentBlockDialog
            showForm={true}
            scheduledTaskId={scheduledTaskId}
            onCancel={(skipRefresh) => {
              updateShowAssessmentBlock(false)

              if (!skipRefresh) {
                setTriggerRefresh(!triggerRefresh)
              }
            }}
          />
        ) : null}
        {showAssessmentForm ? (
          <AssessmentForm
            showForm={showAssessmentForm}
            onCancel={() => {
              setShowAssessmentForm(false)
              setCompletedId(undefined)
            }}
            onSubmit={() => {
              setShowAssessmentForm(false)
              setCompletedId(undefined)
              setTriggerRefresh(!triggerRefresh)
            }}
            assessmentId={newAssessmentId}
            completedAssessmentId={completedId}
            readOnly={openReadOnly}
          />
        ) : null}
        {showScheduleForm ? (
          <AssessmentScheduleForm
            showForm={true}
            onCancel={() => {
              setShowScheduleForm(false)
              setScheduleProfile(undefined)
            }}
            onSaved={() => {
              setTriggerRefresh(!triggerRefresh)
              setShowScheduleForm(false)
              setScheduleProfile(undefined)
            }}
            scheduleId={scheduleProfileId}
            assessments={assessmentProfiles}
            telerecoveryIsActive={activeTelerecovery}
            staffList={staff}
          />
        ) : null}
        <div className={myStyles.pageHeader}>
          <div className={myStyles.pageHeader}>
            <h1>Assessments</h1>
          </div>
          <div className={myStyles.pageHeaderActions}>
            <InactiveParticipantToolTip>
              <Button
                label={
                  tabViewIndex < 2
                    ? "Start new assessment"
                    : "Schedule new assessment"
                }
                type="button"
                className={`${assessStyles.button} ${myStyles.pageHeaderActionButton}`}
                onClick={() => {
                  if (tabViewIndex < 2) {
                    //Note: The below is a temporary measure until the SOAR session block is made available.
                    updateShowAssessmentBlock(true)
                  } else {
                    setShowScheduleForm(true)
                  }
                }}
                disabled={!allowNewAssessment}
              />
            </InactiveParticipantToolTip>
          </div>
        </div>
        <div className={assessStyles.tabView}>
          {!hasParticipantSelection ? (
            <>No participant selected</>
          ) : (
            <TabView
              activeIndex={tabViewIndex}
              onTabChange={(e) => {
                setTabViewIndex(e.index)
              }}
            >
              <TabPanel header={"Overview"}>
                <ParticipantAssessmentsOverview
                  participantServiceEpisode={participantServiceEpisode}
                  triggerRefresh={triggerRefresh}
                  onViewAssessment={(completedAssessmentId) => {
                    setAssessmentId(completedAssessmentId, true)
                  }}
                  facilitatorProfiles={facilitatorProfilesRef.current}
                  onSaveFacilitatorProfile={onSaveFacilitator}
                />
              </TabPanel>
              <TabPanel header={"Completed"}>
                <ParticipantAssessmentsCompleted
                  triggerRefresh={triggerRefresh}
                  onView={(completedAssessmentId) => {
                    setAssessmentId(completedAssessmentId, true, true)
                  }}
                  facilitatorProfiles={facilitatorProfilesRef.current}
                  onSaveFacilitatorProfile={onSaveFacilitator}
                />
              </TabPanel>
              <TabPanel header={"Scheduled"}>
                <ParticipantAssessmentsSchedules
                  triggerRefresh={triggerRefresh}
                  onEdit={(assignmentId) => {
                    setScheduleProfile(assignmentId)
                    setShowScheduleForm(true)
                  }}
                  onProcessScheduledTask={(scheduledTaskId) => {
                    updateShowAssessmentBlock(true, scheduledTaskId)
                  }}
                />
              </TabPanel>
            </TabView>
          )}
        </div>
      </div>
    </>
  )
}
